/**
 * Preparar a imagem da assinatura ANTES de enviá-la.
 *
 * Pedido da Gisele (19/10/2026): "tem que ser possível colocar uma imagem na
 * assinatura de cada operador". A api guarda o arquivo e devolve a URL; quem
 * reduz a imagem é esta tela, por dois motivos:
 *
 * 1. **A api não sabe reduzir.** `ImageHelper::storeBase64Image()` aceita um
 *    tamanho, mas usa `Intervention\Image`, que NÃO está instalado no projeto
 *    (conferido no `composer.json` em 19/10). Passar o tamanho derruba a
 *    requisição com "Class not found".
 * 2. **O corpo da requisição.** Uma foto de celular tem 4 MB; em base64 vira
 *    5,3 MB subindo por uma conexão de operador. Reduzida aqui, sobe em
 *    dezenas de KB.
 */

/** O maior tamanho que faz sentido no fim de uma ação. */
export const LARGURA_MAXIMA = 600;
export const ALTURA_MAXIMA = 200;

/** O que o arquivo pode ser. A api recusa o resto, e aqui o aviso é imediato. */
export const TIPOS_ACEITOS = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];

/**
 * O teto do arquivo ESCOLHIDO, antes de reduzir.
 *
 * Não é o teto do que sobe (isso a redução resolve): é para recusar na hora um
 * arquivo que travaria o navegador ao ser decodificado.
 */
export const TAMANHO_MAXIMO = 10 * 1024 * 1024;

/** O tipo é de imagem que a api aceita? */
export const tipoAceito = (arquivo) => TIPOS_ACEITOS.includes(String(arquivo?.type || '').toLowerCase());

/**
 * As dimensões reduzidas, mantendo a proporção.
 *
 * Imagem menor que o teto NÃO é aumentada: esticar o logo de alguém deixaria
 * a assinatura borrada, e ninguém pediu isso.
 */
export const dimensoesReduzidas = (largura, altura, maxLargura = LARGURA_MAXIMA, maxAltura = ALTURA_MAXIMA) => {
    const l = Number(largura) || 0;
    const a = Number(altura) || 0;

    if (l <= 0 || a <= 0) return { largura: 0, altura: 0 };

    const fator = Math.min(maxLargura / l, maxAltura / a, 1);

    return {
        largura: Math.max(1, Math.round(l * fator)),
        altura: Math.max(1, Math.round(a * fator)),
    };
};

/**
 * O arquivo escolhido, pronto para ir à api: data URL em PNG, já reduzido.
 *
 * PNG e não JPEG porque assinatura costuma ser logo com fundo transparente, e
 * o JPEG o pintaria de preto.
 *
 * @param {File} arquivo
 * @returns {Promise<string>} data URL
 */
export const prepararImagem = (arquivo) => new Promise((resolve, reject) => {
    if (!arquivo) {
        reject(new Error('Nenhum arquivo escolhido.'));
        return;
    }

    if (!tipoAceito(arquivo)) {
        reject(new Error('A imagem precisa ser PNG, JPG, GIF ou WEBP.'));
        return;
    }

    if (arquivo.size > TAMANHO_MAXIMO) {
        reject(new Error('A imagem é muito grande. Escolha um arquivo de até 10 MB.'));
        return;
    }

    const leitor = new FileReader();

    leitor.onerror = () => reject(new Error('Não foi possível ler a imagem.'));
    leitor.onload = () => {
        const img = new Image();

        img.onerror = () => reject(new Error('O arquivo não parece ser uma imagem válida.'));
        img.onload = () => {
            const { largura, altura } = dimensoesReduzidas(img.width, img.height);

            if (largura === 0) {
                reject(new Error('O arquivo não parece ser uma imagem válida.'));
                return;
            }

            try {
                const canvas = document.createElement('canvas');
                canvas.width = largura;
                canvas.height = altura;
                canvas.getContext('2d').drawImage(img, 0, 0, largura, altura);
                resolve(canvas.toDataURL('image/png'));
            } catch (e) {
                // Canvas indisponível (jsdom, navegador antigo): manda o
                // original. A api ainda cobra o formato e o tamanho, então o
                // pior caso é um arquivo maior, não um erro.
                resolve(String(leitor.result || ''));
            }
        };

        img.src = String(leitor.result || '');
    };

    leitor.readAsDataURL(arquivo);
});
